import { useMemo } from 'react';

import { CallOffOrderColor, QuotedColor, UnitPrice } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { ColorIcon } from '@ravago/shared/radiance/components/icon/components/ColorIcon/ColorIcon';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { isCallOffOrderColor } from '../../utils/order-now-utils';

interface Props {
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   value: CallOffOrderColor | QuotedColor | undefined;
   onChange: (value: CallOffOrderColor | QuotedColor) => void;
}

interface OrderNowItem {
   id: string;
   displayName: string;
   type: 'callOff' | 'quote';
   unitPrice: UnitPrice;
   colorName: string;
   originalItem: CallOffOrderColor | QuotedColor;
   expiryDate?: Date;
   isOverdue?: boolean;
   comment?: string;
}

const generateCardId = (value: CallOffOrderColor | QuotedColor, index: number) => {
   if (isCallOffOrderColor(value)) {
      return `calloff-${value.recommendedCallOff.id}`;
   }
   return `quote-${value.quotation.id}-${value.color.name}-${index}`;
};

export const OrderNowSelector = ({ callOffOrderColors, quotedColors, value, onChange }: Props) => {
   const format = useFormatter();
   const translate = useTranslations();

   const items: OrderNowItem[] = useMemo(() => {
      const callOffItems: OrderNowItem[] = callOffOrderColors.map((callOffOrderColor) => ({
         id: generateCardId(callOffOrderColor, 0),
         displayName: callOffOrderColor.recommendedCallOff.id,
         type: 'callOff',
         unitPrice: callOffOrderColor.recommendedCallOff.unitPrice,
         colorName: callOffOrderColor.color.name,
         isOverdue: callOffOrderColor.recommendedCallOff.status === 'Call Off Overdue',
         originalItem: callOffOrderColor
      }));

      const quoteItems: OrderNowItem[] = quotedColors.map((quotedColor, index) => {
         const lowestPriceLine = quotedColor.quotation.lines.reduce(
            (lowest, line) => (line.unitPrice.price.amount < lowest.unitPrice.price.amount ? line : lowest),
            quotedColor.quotation.lines[0]
         );

         const expiryDate = new Date(quotedColor.quotation.effective.until);

         return {
            id: generateCardId(quotedColor, index),
            displayName: quotedColor.quotation.name,
            type: 'quote',
            unitPrice: lowestPriceLine.unitPrice,
            colorName: quotedColor.color.name,
            expiryDate,
            originalItem: quotedColor,
            comment: quotedColor.quotation.description
         };
      });

      return [...callOffItems, ...quoteItems];
   }, [callOffOrderColors, quotedColors]);

   const selectedValue = useMemo(() => {
      if (!value) return '';
      const foundItem = items.find((item) => item.originalItem === value);
      return foundItem?.id || '';
   }, [value, items]);

   const handleChange = (newValue: string) => {
      const foundItem = items.find((item) => item.id === newValue);
      if (foundItem) onChange(foundItem.originalItem);
   };

   return (
      <RadioCardGroup value={selectedValue} onChange={handleChange}>
         {items.map((item) => (
            <RadioCard key={item.id} value={item.id}>
               <Box direction="column" gap="sm">
                  <Box justify="between" items="center">
                     <Box gap="xs" items="center">
                        <Icon icon={item.type === 'quote' ? 'tags' : 'handshake'} />
                        <Typography variant="body-highlight">{item.displayName}</Typography>
                     </Box>
                     <Badge>
                        {item.type === 'quote' ? translate('extended-color-picker.from') : ''} {format.unitPrice(item.unitPrice)}
                     </Badge>
                  </Box>
                  <Box justify="between" items="center">
                     <Box gap="xs" items="center">
                        <ColorIcon colorName={item.colorName.toLowerCase()} />
                        <Typography>{item.colorName}</Typography>
                     </Box>
                     <Box gap="2xs">
                        {item.isOverdue && <Badge variant="error">{translate('extended-color-picker.overdue')}</Badge>}
                        {item.expiryDate && (
                           <Typography>
                              {translate('extended-color-picker.valid-until')} {format.dateTime(item.expiryDate)}
                           </Typography>
                        )}
                     </Box>
                  </Box>
                  {item.type === 'quote' && item.comment && (
                     <Box gap="2xs" items="center">
                        <Icon icon="comment" color="subtle" />
                        <Typography color="subtle">{item.comment}</Typography>
                     </Box>
                  )}
               </Box>
            </RadioCard>
         ))}
      </RadioCardGroup>
   );
};
